import { useEffect, useId, useRef, type ReactNode } from 'react';

interface ModalShellProps {
    isOpen: boolean;
    title: string;
    subtitle?: string;
    onClose: () => void;
    onCloseRequest?: () => void;
    children: ReactNode;
    footer?: ReactNode;
    maxWidth?: 'sm' | 'md' | 'lg';
    elevation?: 'default' | 'nested';
}

const MAX_WIDTH: Record<NonNullable<ModalShellProps['maxWidth']>, string> = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
};

const ELEVATION: Record<NonNullable<ModalShellProps['elevation']>, string> = {
    default: 'z-50',
    nested: 'z-[60]',
};

export default function ModalShell({
    isOpen,
    title,
    subtitle,
    onClose,
    onCloseRequest,
    children,
    footer,
    maxWidth = 'md',
    elevation = 'default',
}: ModalShellProps) {
    const titleId = useId();
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                (onCloseRequest ?? onClose)();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose, onCloseRequest]);

    if (!isOpen) return null;

    const handleBackdropClick = () => {
        (onCloseRequest ?? onClose)();
    };

    return (
        <div className={`fixed inset-0 ${ELEVATION[elevation]} flex items-center justify-center`}>
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={handleBackdropClick}
                aria-hidden="true"
            />

            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className={`relative bg-white rounded-2xl shadow-xl w-full ${MAX_WIDTH[maxWidth]} mx-4 flex flex-col overflow-hidden max-h-[90vh]`}
            >
                <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#e2e8f0]">
                    <div className="min-w-0">
                        <h2 id={titleId} className="text-lg font-semibold text-[#1e293b] truncate">
                            {title}
                        </h2>
                        {subtitle && (
                            <p className="text-sm text-[#64748b] mt-0.5">{subtitle}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => (onCloseRequest ?? onClose)()}
                        className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg text-[#64748b] hover:text-[#1e293b] hover:bg-[#f1f5f9] transition-colors text-xl leading-none"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>

                {footer && (
                    <div className="flex gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-[#fafbfc]">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}
